"use server";

import { cookies, headers } from "next/headers";

import { trackAuthCodeExchangeFailure } from "@/lib/analytics/events";
import { welcomeIfAccountCreating } from "@/lib/auth/first-sign-in";
import {
  oauthFailureDiagnostics,
  posthogDistinctIdFromCookies,
} from "@/lib/auth/oauth-failure-diagnostics";
import { getServerSupabase } from "@/lib/supabase/server";

/** Post-auth work the NATIVE sign-in would otherwise skip.
 *
 *  Every other way into the app establishes its session by way of
 *  /auth/callback, which owns "what happens the first time someone signs
 *  in". Native Sign in with Apple writes the session client-side and never
 *  visits that route, so without these it inherits none of it — silently,
 *  which is the failure mode that surfaces months later as "iOS Apple
 *  signups have a 0% welcome rate".
 *
 *  Server Actions rather than client code because both need server context:
 *  Resend plus `after()` for the email, posthog-node plus the request's own
 *  cookies and headers for the event. */

/** Call after `signInWithIdToken` resolves, when the client's copy of the
 *  user says this sign-in created the account.
 *
 *  Re-reads the user server-side rather than trusting that claim: the
 *  client's word decides whether to spend the round trip, the session
 *  decides who gets the email.
 *
 *  Never throws — the user IS signed in by this point, and failing their
 *  sign-in over a welcome email would be worse than not sending one. */
export async function completeNativeSignIn(): Promise<void> {
  try {
    const supabase = await getServerSupabase();
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) return;
    welcomeIfAccountCreating(data.user);
  } catch (err) {
    console.error("[native-sign-in] post-auth step failed", { err });
  }
}

/** Mirror of the browser flow's failure telemetry.
 *
 *  Same event, and deliberately the same PROPERTY VOCABULARY: the #685
 *  rescue-rate queries filter on `error_code`, so emitting a differently
 *  named key would land the event in PostHog while leaving it outside every
 *  query that matters — invisible in a new way rather than visible.
 *  `oauthFailureDiagnostics` is that vocabulary, reused rather than
 *  re-declared.
 *
 *  The distinct id comes from the caller's own PostHog cookie for the same
 *  reason the callback does it: a constant would collapse every native
 *  failure onto one synthetic person, making "how many users hit this"
 *  unanswerable and keeping it off their session timeline. A Server Action
 *  can read cookies() and headers(), so nothing here is a constraint.
 *
 *  `retried: false` is honest rather than lazy — the #685 auto-retry is a
 *  callback-only mechanism, and the native flow never leaves the webview,
 *  so there is no isolated-context round trip to retry. */
export async function reportNativeSignInFailure(
  provider: string,
  code: string | undefined,
  message: string,
): Promise<void> {
  const cookieJar = (await cookies()).getAll();
  await trackAuthCodeExchangeFailure(
    posthogDistinctIdFromCookies(cookieJar) ?? "anonymous_oauth_failure",
    {
      ...oauthFailureDiagnostics({
        cookieNames: cookieJar.map((c) => c.name),
        headers: await headers(),
        errorCode: code,
        retried: false,
      }),
      // Scopes an alert to one flow or the other. The redirect side emits no
      // `flow` key, so it reads as absent there — not as "redirect".
      flow: "native",
      provider,
      message,
    },
  );
}
